import * as i0 from '@angular/core';
import { Injectable, Optional, SkipSelf, NgZone, Directive, Input, Component, Attribute, Inject, HostListener, NgModule } from '@angular/core';
import { Subject, fromEvent } from 'rxjs';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';

class ScrollReferenceDispatcherService {
    get scrollObservers() {
        return this._scrollObservers;
    }
    get windowScrollObservable() {
        return this._windowScrollObservable;
    }
    constructor(_ngZone) {
        this._ngZone = _ngZone;
        this._scrollableContainer = new Subject();
        this._scrollObservers = new Map();
        this._windowScrollObservable = null;
        if (!this.windowScrollObservable) {
            this._addWindowScrollListener();
        }
    }
    register(scrollableContainer) {
        const scrollObservable = scrollableContainer.elementScroll();
        this._scrollObservers.set(scrollableContainer, scrollObservable);
    }
    deregister(scrollableContainer) {
        const scrollableReference = this._scrollObservers.get(scrollableContainer);
        if (scrollableReference) {
            this._scrollObservers.delete(scrollableContainer);
        }
    }
    _addWindowScrollListener() {
        this._windowScrollObservable = this._ngZone.runOutsideAngular(() => {
            return fromEvent(window.document, 'scroll');
        });
    }
    getAncestorScrollableContainers(elementRef) {
        const scrollableContainers = [];
        // loop through existing containers
        this._scrollObservers.forEach((observable, container) => {
            if (this._hasElementInScrollableContainer(container, elementRef)) {
                scrollableContainers.push(container);
            }
        });
        return scrollableContainers;
    }
    _hasElementInScrollableContainer(scrollableContainerRef, elementRef) {
        let element = elementRef.nativeElement;
        const scrollableContainer = scrollableContainerRef.getElementRef().nativeElement;
        do {
            if (element === scrollableContainer) {
                return true;
            }
            element = element.parentElement;
        } while (element);
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollReferenceDispatcherService, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollReferenceDispatcherService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollReferenceDispatcherService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; } });
// SCROLL DISPATCHER FACTORY
function SCROLL_DISPATCHER_PROVIDER_FACTORY(parentDispatcher, _ngZone) {
    return parentDispatcher || new ScrollReferenceDispatcherService(_ngZone);
}
// SCROLL DISPATCHER PROVIDER SERVICE
const SCROLL_DISPATCHER_PROVIDER = {
    // If there is already a ScrollDispatcher available, use that. Otherwise, provide a new one.
    provide: ScrollReferenceDispatcherService,
    deps: [[new Optional(), new SkipSelf(), ScrollReferenceDispatcherService], NgZone],
    useFactory: SCROLL_DISPATCHER_PROVIDER_FACTORY
};

//import { BackToTopOfContainerComponent } from './back-to-top/back-to-top.component';
class ScrollObserverDirective {
    get currentScrollPosition() {
        return this._scrollPositionInsideContainer;
    }
    constructor(_elementRef, _ngZone, _scrollService) {
        this._elementRef = _elementRef;
        this._ngZone = _ngZone;
        this._scrollService = _scrollService;
        /*@ContentChild(forwardRef(() => BackToTopOfContainerComponent))
        backToTopCmp: BackToTopOfContainerComponent;*/
        this._elementScrolled = new Subject();
        this._scrollListener = (event) => this._elementScrolled.next(event);
    }
    ngOnInit() {
        this._ngZone.runOutsideAngular(() => {
            this.getElementRef().nativeElement.addEventListener("scroll", this._scrollListener);
        });
        this._scrollService.register(this);
    }
    ngAfterViewInit() {
        this._subscription = this._scrollService.scrollObservers
            .get(this)
            .subscribe(event => {
            this._scrollPositionInsideContainer = event.srcElement.scrollTop;
            /*this._ngZone.run(() => {
            this.backToTopCmp.isVisible = this._scrollPositionInsideContainer >= this.backToTopCmp.startPixel ? true : false;
          });*/
        });
    }
    ngOnDestroy() {
        this._scrollService.deregister(this);
        if (this._scrollListener) {
            this.getElementRef().nativeElement.removeEventListener("scroll", this._scrollListener);
        }
        this._subscription.unsubscribe();
    }
    /**
     * Returns observable that emits when a scroll event is fired on the host element.
     */
    elementScroll() {
        return this._elementScrolled.asObservable();
    }
    getElementRef() {
        return this._elementRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollObserverDirective, deps: [{ token: i0.ElementRef }, { token: i0.NgZone }, { token: ScrollReferenceDispatcherService }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: ScrollObserverDirective, selector: "[ant-scroll-container]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollObserverDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[ant-scroll-container]"
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.NgZone }, { type: ScrollReferenceDispatcherService }]; } });

class BackToTopBaseComponent {
    /**
     * isVisible determines whether the component
     * should appear on the screen, or be hidden.
     */
    set isVisible(visible) {
        this._isVisible = visible;
    }
    get isVisible() {
        return this._isVisible;
    }
    /**
     * buttonTitle is the text displayed
     * insdide the button itself, i.e. "Scroll To Top".
     */
    set buttonTitle(title) {
        this._buttonTitle = title;
    }
    get buttonTitle() {
        return this._buttonTitle;
    }
    /**
     * A utility function to determine whether the HTML Id
     * passed to the component is valid, i.e. starts with a '#' and
     * only contains a-z, 0-9, a dash,a word, or a colon,
     * @param id
     */
    isCssIdValid(id) {
        const re = /^\#[A-Za-z0-9]+[\w\-]*$/;
        return re.test(id);
    }
    /**
     * Utility Function: Returns the vertical, pixel position (i.e offset from top)
     * of the "starting" element passed to the component. The
     * component is passed an ID, which is then queried with JQuery.
     */
    getStartingElementPosition() {
        if (!this.isCssIdValid(this._startingElemID)) {
            return -1; // If the ID provided is not CSS-valid, return -1
        }
        const start = $(this._startingElemID);
        if (!start) {
            return -1; //If an element cannot be found with that ID, return -1
        }
        // Otherwise, return the offset from the top of the window.
        return start.offset().top;
    }
    /**
     * Utility Function: Returns the vertical, pixel position (i.e offset from top)
     * of the "stopping" element passed to the component. The
     * component is passed an ID, which is then queried with JQuery.
     */
    getStoppingElementPosition() {
        if (!this.isCssIdValid(this._endingElemID)) {
            return -1; // If the ID provided is not CSS-valid, return -1
        }
        const stop = $(this._endingElemID);
        if (!stop) {
            return -1; //If an element cannot be found with that ID, return -1
        }
        // Otherwise, return the offset from the top of the window.
        return stop.offset().top;
    }
    /**
     * The scroll function responsible for actually moving the user's position in
     * the window or scroll container. JQuery is used here purely for the sake of implementing
     * a smooth scroll. If another animation library is added, JQuery can be removed.
     * @param container An Element reference to the scroll container (either the window object, or a ScrollObserverDirective)
     * @param from The user's current scroll position
     * @param to The position the user wishes to scroll to, i,e, the top of the window
     * @param speed The amount of time it should take to perform the scroll.
     */
    scrollTo(container, from = 0, to, speed) {
        const startPos = from;
        const difference = to - startPos;
        const duration = speed && speed > 0 ? (Math.abs(difference) / speed) * 10 : 'slow';
        $(container).animate({ scrollTop: to }, duration, 'swing');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopBaseComponent, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: BackToTopBaseComponent, inputs: { isVisible: "isVisible", buttonTitle: "buttonTitle" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopBaseComponent, decorators: [{
            type: Directive
        }], propDecorators: { isVisible: [{
                type: Input
            }], buttonTitle: [{
                type: Input
            }] } });
class BackToTopOfContainerComponent extends BackToTopBaseComponent {
    set startPixel(pixelPosition) {
        this._startPixel = pixelPosition;
    }
    get startPixel() {
        return this._startPixel;
    }
    backToTop() {
        this.scrollTo(this._parentObserver.getElementRef().nativeElement, this._parentObserver.currentScrollPosition, 0, null);
    }
    constructor(el, _parentObserver) {
        super();
        this.el = el;
        this._parentObserver = _parentObserver;
        this.isVisible = true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopOfContainerComponent, deps: [{ token: i0.ElementRef }, { token: ScrollObserverDirective }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BackToTopOfContainerComponent, selector: "[data-uxd-back-to-top-of-container]", inputs: { startPixel: "startPixel" }, usesInheritance: true, ngImport: i0, template: `
  <a href="javascript:void(0)" style="position: absolute;" class="round-button back-to-top-dir"
    (click)="backToTop()" [class.hidden]="!isVisible">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    <span>{{buttonTitle}}</span>
  </a>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopOfContainerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-back-to-top-of-container]',
                    template: `
  <a href="javascript:void(0)" style="position: absolute;" class="round-button back-to-top-dir"
    (click)="backToTop()" [class.hidden]="!isVisible">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    <span>{{buttonTitle}}</span>
  </a>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: ScrollObserverDirective }]; }, propDecorators: { startPixel: [{
                type: Input
            }] } });
//istanbul ignore next
class BackToTopComponentV2 extends BackToTopBaseComponent {
    get hasReachedStartPos() {
        return this._hasReachedStartPos;
    }
    get hasReachedStopPos() {
        return this._hasReachedStopPos;
    }
    /**
     * startingElementID
     * Sets tbe starting element id to the provided string, if
     * the string is a css-valid id (a-z,0-9,dashes, underscores).
     */
    set startingElementID(id) {
        if (this.isCssIdValid(id)) {
            this._startingElemID = id;
        }
    }
    /**
     * endingElementID
     * Sets tbe ending element id to the provided string, if
     * the string is a css-valid id (a-z,0-9,dashes, underscores).
     */
    set endingElementID(id) {
        if (this.isCssIdValid(id)) {
            this._endingElemID = id;
        }
    }
    /**
     * startPixel
     * Sets the starting position based on an
     * explicit pixel value, which should be measured
     * from the top of the page.
     */
    set startPixel(distanceFromTop) {
        this._startPixel = distanceFromTop || 0;
    }
    /**
     * startPixel
     * Sets the ending position based on an
     * explicit pixel value, which should be measured
     * from the top of the page.
     */
    set stopPixel(distanceFromTop) {
        this._stopPixel = distanceFromTop || 0;
    }
    constructor(_scrollService) {
        super(); // Call to the parent's constructor ( BackToTopBaseComponent )
        this._scrollService = _scrollService;
        /**
         * TODO: The below component has been temporarily been renamed ‘BackToTopComponentV2’
         * as the original BackToTopComponent is still in use. When safe to do so, this component
         * should be renamed to be the official BackToTopComponent —— no V2.
         */
        /**
         * _hasReachedStartPos
         * Tracks whether the user has
         * scrolled pass the starting position, which will be
         * based on either a provided DOM element (startingElementID)
         * or a fixed pixel value (startPixel).
         */
        this._hasReachedStartPos = false;
        /**
         * _hasReachedStopPos
         * Tracks whether the user has
         * scrolled pass the stopping position, which will be
         * based on either a provided DOM element (endingElementID)
         * or a fixed pixel value (stopPixel).
         */
        this._hasReachedStopPos = false;
        /**
         * The ScrollReferenceDispatcherService maintains the user's scroll position
         * in an observable, which can be unpacked by subscribing to the window scroll.
         */
        let windowPositionY = 0;
        this._subscription = this._scrollService.windowScrollObservable.subscribe((event) => {
            //TODO: Get the page offset based off of the event, instead of window.pageYOffset
            windowPositionY = window.pageYOffset;
            this._hasReachedStartPos = this.onStartPixel(windowPositionY + window.innerHeight);
            this._hasReachedStopPos = this.onStopPixel(windowPositionY + window.innerHeight);
        });
    }
    /**
     * onStartPixel is a callback function used to determine
     * whether the user has scrolled pass the start position yet.
     * @param windowPositionY The current page offset from the top
     */
    onStartPixel(windowPositionY) {
        return windowPositionY >= this._startPixel ? true : false;
    }
    /**
     * onStopPixel is a callback function used to determine
     * whether the user to the stopping position.
     * @param windowPositionY The current page offset from the top
     */
    onStopPixel(windowPositionY) {
        return windowPositionY >= this._stopPixel ? true : false;
    }
    ngAfterViewInit() {
        /**
         * The position / offset of the prescribed elements may only be determined
         * after the view has succesffuly rendered; If done beforehand, both the start and
         * stop values will be undefined.
         */
        if (this.getStartingElementPosition() > -1) {
            this._startPixel = this.getStartingElementPosition();
        }
        if (this.getStoppingElementPosition() > -1) {
            this._stopPixel = this.getStoppingElementPosition();
        }
    }
    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
    scrollToTopOfWindow() {
        // Scroll the documentElement back to top of the page (i.e. offset = 0)
        this.scrollTo(document.documentElement, 0, 0, null);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopComponentV2, deps: [{ token: ScrollReferenceDispatcherService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BackToTopComponentV2, selector: "[data-uxd-back-to-top-cmp-v2]", inputs: { startingElementID: "startingElementID", endingElementID: "endingElementID", startPixel: "startPixel", stopPixel: "stopPixel" }, usesInheritance: true, ngImport: i0, template: `
  <a href="javascript:void(0)" class="round-button back-to-top-dir"
    [ngClass]="{'backtotop-fixed': hasReachedStopPos,
                'hidden': !hasReachedStartPos || hasReachedStopPos }"
    (click)="scrollToTopOfWindow()">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    <span>{{buttonTitle}}</span>
  </a>`, isInline: true, dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopComponentV2, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-back-to-top-cmp-v2]',
                    template: `
  <a href="javascript:void(0)" class="round-button back-to-top-dir"
    [ngClass]="{'backtotop-fixed': hasReachedStopPos,
                'hidden': !hasReachedStartPos || hasReachedStopPos }"
    (click)="scrollToTopOfWindow()">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    <span>{{buttonTitle}}</span>
  </a>`
                }]
        }], ctorParameters: function () { return [{ type: ScrollReferenceDispatcherService }]; }, propDecorators: { startingElementID: [{
                type: Input
            }], endingElementID: [{
                type: Input
            }], startPixel: [{
                type: Input
            }], stopPixel: [{
                type: Input
            }] } });

//istanbul ignore next
class BackToTopComponent {
    constructor(startPoint, buttonText, pixelFromTopPosition, renderer) {
        this.renderer = renderer;
        this.showButton = false;
        this.startPixel = startPoint;
        this.buttonTitle = buttonText;
        this.pixelFromTop = pixelFromTopPosition;
    }
    ngOnInit() {
        this.renderer.listen('window', 'scroll', (event) => {
            if (!$('.back-to-top-dir').offset() || !$('.mbr-bottom-of-page').offset()) {
                return;
            }
            if (window.pageYOffset >= this.startPixel) {
                if ($('.back-to-top-dir').offset().top + $('.back-to-top-dir').height() >= $('.mbr-bottom-of-page').offset().top - 15) {
                    $('.back-to-top-dir').addClass('backtotop-fixed');
                }
                this.showButton = true;
            }
            else {
                this.showButton = false;
            }
            if ($(document).scrollTop() + window.innerHeight < $('.mbr-bottom-of-page').offset().top + 30) {
                $('.back-to-top-dir').removeClass('backtotop-fixed');
            }
        });
    }
    scrollToPosition() {
        $('html, body').animate({ scrollTop: this.pixelFromTop }, 'slow');
        $('.ant-menu-toggle').focus();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopComponent, deps: [{ token: 'data-starting-pixel', attribute: true }, { token: 'data-button-title', attribute: true }, { token: 'data-pixel-from-top', attribute: true }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BackToTopComponent, selector: "[data-uxd-back-to-top-cmp]", ngImport: i0, template: `<a href="javascript:void(0)" class="round-button back-to-top-dir" (click)="scrollToPosition()" [hidden]="!showButton">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    {{buttonTitle}}
</a>`, isInline: true, styles: ["[hidden]{display:none!important}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopComponent, decorators: [{
            type: Component,
            args: [{ selector: '[data-uxd-back-to-top-cmp]', template: `<a href="javascript:void(0)" class="round-button back-to-top-dir" (click)="scrollToPosition()" [hidden]="!showButton">
    <span class="fa fa-arrow-up black" aria-hidden="true"></span>
    {{buttonTitle}}
</a>`, styles: ["[hidden]{display:none!important}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Attribute,
                    args: ['data-starting-pixel']
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['data-button-title']
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['data-pixel-from-top']
                }] }, { type: i0.Renderer2 }]; } });

//istanbul ignore next
class SmoothScrollDirective {
    constructor(_element, _window, renderer) {
        this._element = _element;
        this._window = _window;
        this.renderer = renderer;
        this._jElement = $(this._element.nativeElement);
    }
    onClick(event, target) {
        this.options = this._jElement.find('li a');
        event.preventDefault();
        this.htmlBody = this._jElement
            .offsetParent()
            .offsetParent()
            .find('body');
        let id = target.attributes[target.attributes.length - 1].nodeValue;
        let element = this.htmlBody.find(id);
        if (element.length) {
            let offset = element.offset().top;
            this._window.scrollTo({ top: offset, behavior: 'smooth' });
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SmoothScrollDirective, deps: [{ token: i0.ElementRef }, { token: 'Window' }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: SmoothScrollDirective, selector: "[data-uxd-smooth-scroll-dir]", host: { listeners: { "click": "onClick($event,$event.target)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SmoothScrollDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[data-uxd-smooth-scroll-dir]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: Window, decorators: [{
                    type: Inject,
                    args: ['Window']
                }] }, { type: i0.Renderer2 }]; }, propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event', '$event.target']]
            }] } });

class BackToTopButtonComponent {
    constructor(_scrollService) {
        this._scrollService = _scrollService;
        this._hideButton = true;
        /**
         * The ScrollReferenceDispatcherService maintains the user's scroll position
         * in an observable, which can be unpacked by subscribing to the window scroll.
         */
        let windowPositionY = 0;
        this._subscription = this._scrollService.windowScrollObservable.subscribe((event) => {
            //TODO: Get the page offset based off of the event, instead of window.pageYOffset
            windowPositionY = window.pageYOffset;
            this._hideButton = windowPositionY >= this.showButtonAtPx ? false : true;
        });
    }
    ngOnInit() {
        this._bodyEle = window.document.querySelector('body');
    }
    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
    scrollToTopOfWindow() {
        this.fadeOut(this._bodyEle, this.fadeIn);
    }
    fadeOut(element, callback) {
        let op = 1; // initial opacity
        let increment = .05;
        let timer = setInterval(() => {
            if (op <= 0) {
                clearInterval(timer);
                document.documentElement.scrollTop = 0;
                callback(element);
            }
            element.style.opacity = op;
            element.style.filter = 'alpha(opacity=' + op + ')';
            op = (op - increment);
        }, 10);
    }
    fadeIn(element) {
        let op = 0; // initial opacity
        let increment = .05;
        let timer = setInterval(() => {
            if (op >= 1) {
                clearInterval(timer);
            }
            element.style.opacity = op;
            element.style.filter = 'alpha(opacity=' + op + ')';
            op = (op + increment);
        }, 10);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopButtonComponent, deps: [{ token: ScrollReferenceDispatcherService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BackToTopButtonComponent, selector: "uxd-back-to-top-cmp", inputs: { buttonTitle: "buttonTitle", showButtonAtPx: "showButtonAtPx" }, ngImport: i0, template: `
  <a href="javascript:void(0)" class="back-to-top-button"
    [ngClass]="{'hidden': _hideButton}"
    (click)="scrollToTopOfWindow()">
    <span class="motif-icon motif-up-arrow" aria-hidden="true"></span>
    <span [innerHTML]="buttonTitle"></span>
  </a>
 `, isInline: true, dependencies: [{ kind: "directive", type: i3.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BackToTopButtonComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'uxd-back-to-top-cmp',
                    template: `
  <a href="javascript:void(0)" class="back-to-top-button"
    [ngClass]="{'hidden': _hideButton}"
    (click)="scrollToTopOfWindow()">
    <span class="motif-icon motif-up-arrow" aria-hidden="true"></span>
    <span [innerHTML]="buttonTitle"></span>
  </a>
 `
                }]
        }], ctorParameters: function () { return [{ type: ScrollReferenceDispatcherService }]; }, propDecorators: { buttonTitle: [{
                type: Input
            }], showButtonAtPx: [{
                type: Input
            }] } });

class JumpToModule {
    static forRoot() {
        return {
            ngModule: JumpToModule
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: JumpToModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: JumpToModule, declarations: [ScrollObserverDirective,
            SmoothScrollDirective,
            BackToTopComponent,
            BackToTopComponentV2,
            BackToTopOfContainerComponent,
            BackToTopButtonComponent], imports: [CommonModule], exports: [ScrollObserverDirective,
            SmoothScrollDirective,
            BackToTopComponent,
            BackToTopComponentV2,
            BackToTopOfContainerComponent,
            BackToTopButtonComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: JumpToModule, providers: [SCROLL_DISPATCHER_PROVIDER], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: JumpToModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        ScrollObserverDirective,
                        SmoothScrollDirective,
                        BackToTopComponent,
                        BackToTopComponentV2,
                        BackToTopOfContainerComponent,
                        BackToTopButtonComponent
                    ],
                    exports: [
                        ScrollObserverDirective,
                        SmoothScrollDirective,
                        BackToTopComponent,
                        BackToTopComponentV2,
                        BackToTopOfContainerComponent,
                        BackToTopButtonComponent
                    ],
                    providers: [SCROLL_DISPATCHER_PROVIDER]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BackToTopBaseComponent, BackToTopButtonComponent, BackToTopComponent, BackToTopComponentV2, BackToTopOfContainerComponent, JumpToModule, SCROLL_DISPATCHER_PROVIDER, SCROLL_DISPATCHER_PROVIDER_FACTORY, ScrollObserverDirective, ScrollReferenceDispatcherService, SmoothScrollDirective };
